import { blockedPopup, versionChangedPopup } from 'libs/DB/BaseDB'

interface ContactData {
  serverId: string
  etag: string
  emails: string
  phones: string
  allValues: string
  contact: any
}

interface AddressBookData {
  url: string
  syncToken?: string
  displayName?: string
  ctag?: string
  isSync: boolean
  isDefault: boolean
  folderType: string
}

interface PhotoData {
  serverId: string
  photo: string
}

interface PromiseResolveReject {
  resolve: (value: any) => void
  reject: (reason?: any) => void
}

type TableName = 'contact' | 'addressBook' | 'photo'

class PersonalContactDB {
  isActive: boolean = false
  db: IDBDatabase | null = null
  loaded: boolean = false
  autoInit: boolean = true
  isInitialing: boolean = false
  initWaitingReq: PromiseResolveReject[] = []

  private dbName: string = 'personalContacts'
  private contactTableName: string = 'contact'
  private addressBookTableName: string = 'addressBook'
  private photoTableName: string = 'photo'
  private version: number = 1

  constructor() {
    if (this.autoInit === true) {
      this.init()
    }
  }

  private init(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isInitialing !== true) {
        this.isInitialing = true

        const DBOpenRequest = window.indexedDB.open(this.dbName, this.version)

        DBOpenRequest.onerror = (error) => {
          console.error('error - db open failed', error)
          this.isActive = false
        }

        DBOpenRequest.onsuccess = (event) => {
          console.log('success - db open success.')

          this.db = (event.target as IDBOpenDBRequest).result

          // 다른 탭이 새 버전으로 upgrade를 요청하면 이 연결을 닫아야 해당 탭의 open이 block되지 않는다.
          this.db.onversionchange = () => {
            console.warn('warning - db version changed by another tab.', this.dbName)
            this.db?.close()
            this.db = null
            this.isActive = false
            this.loaded = false
            versionChangedPopup()
          }

          this.isActive = true

          this.initWaitingReq.forEach((promise) => {
            promise.resolve(true)
          })
          this.initWaitingReq = []

          this.isInitialing = false
          resolve(true)
        }

        DBOpenRequest.onblocked = (event) => {
          console.error('error - db blocked.', event)
          blockedPopup()
        }

        DBOpenRequest.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result

          db.onerror = (error) => {
            console.error('error - db upgrade.', error)
            this.isActive = false

            this.initWaitingReq.forEach((promise) => {
              promise.reject(error)
            })
            this.initWaitingReq = []

            this.isInitialing = false
            reject(error)
          }

          if (event.oldVersion != null && event.oldVersion > 0) {
            try {
              db.deleteObjectStore(this.contactTableName)
              db.deleteObjectStore(this.addressBookTableName)
              db.deleteObjectStore(this.photoTableName)
            } catch (e) {
              console.log('error - db table delete failed.', e)
            }
          }

          const contactObjectStore = db.createObjectStore(this.contactTableName, {
            keyPath: 'serverId',
          })
          contactObjectStore.createIndex('emails', 'emails', { unique: false })
          contactObjectStore.createIndex('phones', 'phones', { unique: false })
          contactObjectStore.createIndex('allValues', 'allValues', { unique: false })

          db.createObjectStore(this.photoTableName, {
            keyPath: 'serverId',
          })

          db.createObjectStore(this.addressBookTableName, {
            keyPath: 'url',
          })
        }
      } else {
        this.initWaitingReq.push({
          resolve,
          reject,
        })
      }
    })
  }

  close(): void {
    if (this.isActive === true && this.db) {
      this.db.close()
    }
    this.isActive = false
  }

  addData(
    tableName: TableName,
    data: ContactData | AddressBookData | PhotoData,
  ): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.add(data)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Added successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to add.', error)
        reject(error)
      }
    })

    return promise
  }

  putData(
    tableName: TableName,
    data: ContactData | AddressBookData | PhotoData,
  ): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.put(data)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Added successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to add.', error)
        reject(error)
      }
    })

    return promise
  }

  deleteData(tableName: TableName, key: string): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.delete(key)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Deleted successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to delete.', error)
        reject(error)
      }
    })

    return promise
  }

  clear(tableName: TableName): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.clear()

      objectStoreRequest.onsuccess = (event) => {
        console.log('cleared successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to clear.', error)
        reject(error)
      }
    })

    return promise
  }

  getData(tableName: TableName, key: string): Promise<any> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.get(key)

      objectStoreRequest.onsuccess = (/* event */) => {
        const { result } = objectStoreRequest

        if (result != null) {
          resolve(result)
        } else {
          console.log('No matching data.', tableName, key)
          resolve(null)
        }
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to get.', error)
        reject(error)
      }
    })

    return promise
  }

  getAllData(tableName: TableName): Promise<any[]> {
    if (this.isActive !== true) {
      return Promise.resolve([])
    }

    const promise = new Promise<any[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(tableName)

      const cursorRequest = objectStore.openCursor()

      const dataList: any[] = []
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result
        if (cursor) {
          dataList.push(cursor.value)
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }

      cursorRequest.onerror = (error) => {
        console.log('failed to getAll.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContact(searchText: string | null): Promise<ContactData[] | null> {
    if (this.isActive !== true || searchText == null) {
      return Promise.resolve(null)
    }

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('allValues')

      const dataList: ContactData[] = []
      const searchTextLower = searchText.toLowerCase()

      const request = index.openCursor()
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor) {
          if ((cursor.key as string).includes(searchTextLower)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContactByEmail(email: string | null, count: number = -1): Promise<ContactData[] | null> {
    if (this.isActive !== true || email == null) {
      return Promise.resolve(null)
    }

    const inCount = count <= 0 ? Number.MAX_SAFE_INTEGER : count

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('emails')

      const targetEmail = email.toLowerCase() + '\n'
      const request = index.openCursor()
      const dataList: ContactData[] = []
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor && dataList.length < inCount) {
          if ((cursor.key as string).includes(targetEmail)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContactNameEmail(text: string | null, count: number = -1): Promise<ContactData[] | null> {
    if (this.isActive !== true || text == null) {
      return Promise.resolve(null)
    }

    const inCount = count <= 0 ? Number.MAX_SAFE_INTEGER : count

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('emails')

      const targetText = text.toLowerCase()
      const request = index.openCursor()
      const dataList: ContactData[] = []
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor && dataList.length < inCount) {
          if ((cursor.key as string).includes(targetText)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContactByPhone(phone: string | null, count: number = -1): Promise<ContactData[] | null> {
    if (this.isActive !== true || phone == null) {
      return Promise.resolve(null)
    }

    const inCount = count <= 0 ? Number.MAX_SAFE_INTEGER : count

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('phones')

      const targetPhone = phone.toLowerCase()
      const request = index.openCursor()
      const dataList: ContactData[] = []
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor && dataList.length < inCount) {
          if ((cursor.key as string).includes(targetPhone)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  async getDB(): Promise<boolean> {
    if (this.isActive === true) {
      return Promise.resolve(true)
    }
    const initRes = await this.init()
    return initRes
  }
}

export default PersonalContactDB
